import { OperatorWithStatus } from "gql/generated";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useAlphabet, useOperatorFilter } from "shared/hooks";

type UseListItemsProps = {
  operators: OperatorWithStatus[];
  onOperatorEntityIdsChange: (operatorVersionIds: string[]) => void;
};

export const useListItems = ({
  operators = [],
  onOperatorEntityIdsChange,
}: UseListItemsProps) => {
  // Store the initial operator set (to be used for filtering)
  const initialOperators = useMemo(() => operators, [operators]);

  // Setup filtering, searching, and alphabet grouping
  const { filterSearchValue } = useOperatorFilter();
  const [filteredOperators, setFilteredOperators] =
    useState<OperatorWithStatus[]>(initialOperators);

  // Set filtered operators again when a change is made (update)
  useEffect(() => {
    if (initialOperators) {
      setFilteredOperators(initialOperators);
    }
  }, [initialOperators]);

  const {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    createAlphabetGroups,
  } = useAlphabet();

  /**
   * Handle searching
   */
  const [searchValue, setSearchValue] = useState("");
  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);

    const filtered = filterSearchValue(initialOperators, searchVal);
    setFilteredOperators(filtered);
    createAlphabetGroups(filtered);
  };

  /**
   * Create the initial alphabet grouping
   */
  useEffect(() => {
    createAlphabetGroups(initialOperators);
  }, [initialOperators, createAlphabetGroups]);

  /**
   * Get a list of the unique operator version IDs to be used when
   * getting the export to CSV data
   */
  useEffect(() => {
    onOperatorEntityIdsChange(
      alphabetGroups.flatMap((group) =>
        group.operators.map((operator) => operator._entityId),
      ),
    );
  }, [alphabetGroups, onOperatorEntityIdsChange]);

  return {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    numOperators: filteredOperators.length,
    searchValue,
    showAlphabet: filteredOperators.length > 25,
    onSearchValueChange: handleSearchValueChange,
  };
};
